import { reducerWithInitialState } from "../util/redux/typed-reducers";
import {
    BasicInfoDto,
    PersonInfoDto,
    LoanFinancingDto,
    IncomeOtherLoansDto,
    LivingExpensesDto,
    AgreementsInfoDto,
} from "../util/DTOs/MortgageDtos";
import { ReduxDispatch, ReduxGetState, ReduxState } from "./reducers";
import logger from "../util/logger";
import { env } from "../util/env";
import * as request from "../util/request";
import actionCreatorFactory from "../util/redux/action-factory";
import {
    formValueSelector,
    isValid,
    isDirty,
    getFormValues,
    reset,
} from "redux-form";
import { showSnackbar } from "./snackbar";
import {
    SelectOptions,
    OptionGroup,
    Options,
    Country,
    ApplicationStatusDto,
} from "../util/interfaces";
import LoanFinancing from "../views/loans/mortgage/components/LoanFinancing";
import BasicInfo from "../views/loans/mortgage/components/BasicInfo";

export interface IMortgageState {
    loanId: number;
    basicInfo: BasicInfoDto;
    primaryApplicant: PersonInfoDto;
    secondaryApplicant: PersonInfoDto;
    loanFinancing: LoanFinancingDto;
    incomeOtherLoans: IncomeOtherLoansDto;
    livingExpenses: LivingExpensesDto;
    agreementsInfo: AgreementsInfoDto;
    loading: boolean;
    choiceItems?: SelectOptions;
    countries?: Country[];
    twoPersons: boolean;
}

/**
 * MORTGAGE FORM NAMES
 */
export const basicInfoFormName = "basicInfo";
export const primaryApplicantFormName = "primaryApplicant";
export const secondaryApplicantFormName = "secondaryApplicant";
export const loanFinancingFormName = "loanFinancing";
export const incomeOtherLoansFormName = "incomeOtherLoans";
export const livingExpensesFormName = "livingExpensesInfo";
export const agreementsFormName = "agreementsInfo";

const actionFactory = actionCreatorFactory("HypoBongari/Loan");

const getApplicationStatusAction = actionFactory<
    {},
    ApplicationStatusDto,
    string
>("GET_APPLICATION_STATUS");

const getMortgageDataAction = actionFactory<{}, IMortgageState, string>(
    "GET_MORTGAGE_DATA"
);

const postMortgageApplication = actionFactory<{}, BasicInfoDto, string>(
    "SEND_MORTGAGE_APPLICATION"
);

const postBasicInfoDataAction = actionFactory<{}, BasicInfoDto, string>(
    "BASIC_INFO_DATA"
);

const postPrimaryApplicantDataAction = actionFactory<{}, PersonInfoDto, string>(
    "PRIMARY_APPLICANT_DATA"
);

const postSecondaryApplicantDataAction = actionFactory<
    {},
    PersonInfoDto,
    string
>("SECONDARY_APPLICANT_DATA");

const deleteSecondaryApplicantAction = actionFactory<{}, string, string>(
    "DELETE_SECONDARY_APPLICANT"
);

const postLoanFinancingDataAction = actionFactory<{}, LoanFinancingDto, string>(
    "LOAN_FINANCING_DATA"
);
export const SetChoiceItems = actionFactory<undefined, SelectOptions>(
    "SET_CHOICE_ITEMS"
);
export const SetCountries = actionFactory<undefined, Country[]>(
    "SET_COUNTRIES"
);

const postIncomeOtherLoansDataAction = actionFactory<
    {},
    IncomeOtherLoansDto,
    string
>("INCOME_OTHER_LOANS_DATA");

const postLivingExpensesDataAction = actionFactory<
    {},
    LivingExpensesDto,
    string
>("LIVING_EXPENSES_DATA");

const postAgreementsInfoDataAction = actionFactory<
    {},
    AgreementsInfoDto,
    string
>("AGREEMENTS_INFO_DATA");

const setTwoPersonsAction = actionFactory<boolean>("SET_TWO_PERSONS");

export function getMortgageInfo(id?: number) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(getMortgageDataAction({}));

            const loanId = id || getState().mortgage.loanId;
            let response = await request.get<any>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/full`
            );

            if (response.loanApplication.canEdit) {
                response = {
                    ...response,
                    basicInfo: response.loanApplication,
                    agreementsInfo: response.agreements,
                    loanFinancing: response.financing,
                    loanId: response.loanApplication.id,
                    secondaryApplicant: response.secondApplicant,
                };
            } else {
                response = {};
            }

            dispatch(getMortgageDataAction.done(response));
        } catch (error) {
            logger.error(error);
            dispatch(
                getMortgageDataAction.failed(
                    "Lainatietojen hakeminen epäonnistui"
                )
            );
            dispatch(showSnackbar("Lainatietojen hakeminen epäonnistui"));
        }
    };
}

export function getApplicationStatus(id?: number) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(getApplicationStatusAction({}));

            const loanId = id || getState().mortgage.loanId;
            let response: ApplicationStatusDto;

            if (loanId === undefined || loanId === -1) {
                response = await request.get<ApplicationStatusDto>(
                    `${env.APP_API_HOST}/api/loanApplications/status/`
                );
            } else {
                response = await request.get<ApplicationStatusDto>(
                    `${env.APP_API_HOST}/api/loanApplications/status/?loanId=${loanId}`
                );
            }

            dispatch(getApplicationStatusAction.done(response));
        } catch (error) {
            logger.error(error);
            dispatch(
                getApplicationStatusAction.failed(
                    "Lainatietojen hakeminen epäonnistui"
                )
            );

            if (error.status !== 404) {
                dispatch(showSnackbar("Lainatietojen hakeminen epäonnistui"));
            }
            throw error;
        }
    };
}

export function sendAgreementsInfoForm(form: AgreementsInfoDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postAgreementsInfoDataAction({}));
            const loanId = getState().mortgage.loanId;

            const dto = {
                emailUse: String(form.emailUse) === "1",
                directMarketing: String(form.directMarketing) === "1",
                politicalConnections: String(form.politicalConnections) === "1",
                politicalConnectionsInfo: form.politicalConnectionsInfo,
            } as AgreementsInfoDto;

            const response = await request.post<AgreementsInfoDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/agreements`,
                dto
            );

            dispatch(postAgreementsInfoDataAction.done(response));
            dispatch(reset(agreementsFormName));
        } catch (error) {
            logger.error(error);
            dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            dispatch(postAgreementsInfoDataAction.failed(""));
            throw error;
        }
    };
}

export function sendLivingExpensesForm(form: LivingExpensesDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postLivingExpensesDataAction({}));

            const loanId = getState().mortgage.loanId;

            const response = await request.post<LivingExpensesDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/livingExpenses`,
                form
            );

            dispatch(postLivingExpensesDataAction.done(response));
            dispatch(reset("livingExpensesInfo"));
        } catch (error) {
            logger.error(error);
            dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            dispatch(postLivingExpensesDataAction.failed(""));
            throw error;
        }
    };
}

export function sendIncomeOtherLoansForm(form: IncomeOtherLoansDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postIncomeOtherLoansDataAction({}));

            const loanId = getState().mortgage.loanId;

            const response = await request.post<IncomeOtherLoansDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/incomeOtherLoans`,
                form
            );

            dispatch(postIncomeOtherLoansDataAction.done(response));
            dispatch(reset(incomeOtherLoansFormName));
        } catch (error) {
            logger.error(error);
            dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            dispatch(postIncomeOtherLoansDataAction.failed(""));
            throw error;
        }
    };
}

export function sendLoanFinancingForm(form: LoanFinancingDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postLoanFinancingDataAction({}));

            const loanId = getState().mortgage.loanId;
            const dto = {
                ...form,
                currentApartmentStatusId:
                    form.currentApartmentStatusId === ""
                        ? null
                        : form.currentApartmentStatusId,
                secondaryCollateralId:
                    form.secondaryCollateralId === ""
                        ? null
                        : form.secondaryCollateralId,
            } as LoanFinancingDto;

            const response = await request.post<LoanFinancingDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/financing`,
                dto
            );

            dispatch(postLoanFinancingDataAction.done(response));
            dispatch(reset(loanFinancingFormName));
        } catch (error) {
            logger.error(error);
            dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            dispatch(postLoanFinancingDataAction.failed(""));
            throw error;
        }
    };
}

export function sendSecondaryApplicantForm(form: PersonInfoDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postSecondaryApplicantDataAction({}));
            const loanId = getState().mortgage.loanId;

            form = {
                ...form,
                contactByPhone: form.contactByPhone || false,
                contactByEmail: form.contactByEmail || false,
            };

            const response = await request.post<PersonInfoDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/applicants`,
                form
            );

            dispatch(postSecondaryApplicantDataAction.done(response));
            dispatch(reset(secondaryApplicantFormName));
        } catch (error) {
            logger.error(error);
            dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            dispatch(postSecondaryApplicantDataAction.failed(""));
            throw error;
        }
    };
}

export function deleteSecondaryApplicant() {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(deleteSecondaryApplicantAction({}));
            const loanId = getState().mortgage.loanId;

            const response = await request.deleteResource<string>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/applicants/deleteSecondApplicant`
            );

            dispatch(deleteSecondaryApplicantAction.done(response));
        } catch (error) {
            logger.error(error);
            dispatch(deleteSecondaryApplicantAction.failed(""));
            throw error;
        }
    };
}

export function sendPrimaryApplicantForm(form: PersonInfoDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postPrimaryApplicantDataAction({}));

            const loanId = getState().mortgage.loanId;
            form = {
                ...form,
                contactByPhone: form.contactByPhone || false,
                contactByEmail: form.contactByEmail || false,
                firstName: getState().user.firstName || "etu",
                lastName: getState().user.lastName || "suku",
                isPrimaryApplicant: true,
            };

            const response = await request.post<PersonInfoDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/applicants`,
                form
            );

            dispatch(postPrimaryApplicantDataAction.done(response));
            dispatch(reset(primaryApplicantFormName));
        } catch (error) {
            logger.error(error);
            dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            dispatch(postPrimaryApplicantDataAction.failed(""));
            throw error;
        }
    };
}

export function sendMortgageBasicInfoForm(form: BasicInfoDto) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postBasicInfoDataAction({}));

            form = {
                ...form,
            };

            const loanId = getState().mortgage.loanId;
            let response: BasicInfoDto;

            if (loanId && loanId !== -1) {
                response = await request.post<BasicInfoDto>(
                    `${env.APP_API_HOST}/api/loanApplications/${loanId}`,
                    form
                );
            } else {
                response = await request.post<BasicInfoDto>(
                    `${env.APP_API_HOST}/api/loanApplications/`,
                    form
                );
            }

            dispatch(postBasicInfoDataAction.done(response));
            dispatch(reset(basicInfoFormName));
        } catch (error) {
            logger.error(error);
            dispatch(postBasicInfoDataAction.failed(""));

            if (
                error.status === 400 &&
                error.message === "Cannot edit sent loan application"
            ) {
                dispatch(showSnackbar("Lähetettyä hakemusta ei voi muokata"));
            } else if (
                error.status === 400 &&
                error.message === "User already has an open application"
            ) {
                dispatch(showSnackbar("Sinulla on keskeneräinen lainahakemus"));
            } else {
                dispatch(showSnackbar("Tietojen tallennus epäonnistui"));
            }

            throw error;
        }
    };
}

export function sendMortgageApplication() {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            dispatch(postMortgageApplication({}));

            const loanId = getState().mortgage.loanId;
            const response = await request.post<BasicInfoDto>(
                `${env.APP_API_HOST}/api/loanApplications/${loanId}/send`,
                {}
            );

            dispatch(postMortgageApplication.done(response));
        } catch (error) {
            logger.error(error);
            dispatch(
                postMortgageApplication.failed("Hakemuksen lähetys epäonnistui")
            );
            dispatch(showSnackbar("Hakemuksen lähetys epäonnistui"));
            throw error;
        }
    };
}

export function sendDirtyAndValidMortgageForms(forms: { [key: string]: any }) {
    return async (
        dispatch: ReduxDispatch,
        getState: ReduxGetState
    ): Promise<void> => {
        try {
            Object.keys(forms).forEach((key) => {
                switch (key) {
                    case basicInfoFormName:
                        const basicInfo = forms[key] as BasicInfoDto;
                        dispatch(sendMortgageBasicInfoForm(basicInfo));
                        break;
                    case primaryApplicantFormName:
                        const primary = forms[key] as PersonInfoDto;
                        dispatch(sendPrimaryApplicantForm(primary));
                        break;
                    case secondaryApplicantFormName:
                        const secondary = forms[key] as PersonInfoDto;
                        dispatch(sendSecondaryApplicantForm(secondary));
                        break;
                    case loanFinancingFormName:
                        const financing = forms[key] as LoanFinancingDto;
                        dispatch(sendLoanFinancingForm(financing));
                        break;
                    case incomeOtherLoansFormName:
                        const income = forms[key] as IncomeOtherLoansDto;
                        dispatch(sendIncomeOtherLoansForm(income));
                        break;
                    case livingExpensesFormName:
                        const expenses = forms[key] as LivingExpensesDto;
                        dispatch(sendLivingExpensesForm(expenses));
                        break;
                    case agreementsFormName:
                        const agreements = forms[key] as AgreementsInfoDto;
                        dispatch(sendAgreementsInfoForm(agreements));
                        break;
                    default:
                        break;
                }
            });
        } catch (error) {
            logger.error(error);
        }
    };
}

const initialCommonInfo: IMortgageState = {
    loanId: -1,
    basicInfo: {} as BasicInfoDto,
    primaryApplicant: {} as PersonInfoDto,
    secondaryApplicant: {} as PersonInfoDto,
    loanFinancing: {} as LoanFinancingDto,
    incomeOtherLoans: {} as IncomeOtherLoansDto,
    livingExpenses: {} as LivingExpensesDto,
    agreementsInfo: {} as AgreementsInfoDto,
    loading: false,
    twoPersons: false,
};

export const LoanReducer = reducerWithInitialState(initialCommonInfo)
    .case(getMortgageDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getMortgageDataAction.done, (state, action) => {
        const secondApplicant =
            action.payload.secondaryApplicant &&
            Object.keys(action.payload.secondaryApplicant).length > 0;
        return {
            ...state,
            basicInfo: action.payload.basicInfo,
            primaryApplicant: action.payload.primaryApplicant,
            secondaryApplicant: action.payload.secondaryApplicant,
            loanFinancing: action.payload.loanFinancing,
            incomeOtherLoans: action.payload.incomeOtherLoans,
            livingExpenses: action.payload.livingExpenses,
            agreementsInfo: action.payload.agreementsInfo,
            loanId: action.payload.loanId,
            loading: false,
            twoPersons: secondApplicant,
        };
    })
    .case(getMortgageDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(getApplicationStatusAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getApplicationStatusAction.done, (state, action) => {
        return {
            ...state,
            loanId: action.payload.id,
            loading: false,
        };
    })
    .case(getApplicationStatusAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postBasicInfoDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postBasicInfoDataAction.done, (state, action) => {
        return {
            ...state,
            loanId: Number(action.payload.id),
            basicInfo: action.payload as BasicInfoDto,
            loading: false,
        };
    })
    .case(postBasicInfoDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postPrimaryApplicantDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postPrimaryApplicantDataAction.done, (state, action) => {
        return {
            ...state,
            primaryApplicant: action.payload as PersonInfoDto,
            loading: false,
        };
    })
    .case(postPrimaryApplicantDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postSecondaryApplicantDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
            twoPersons: true,
        };
    })
    .case(postSecondaryApplicantDataAction.done, (state, action) => {
        return {
            ...state,
            secondaryApplicant: action.payload as PersonInfoDto,
            loading: false,
            twoPersons: true,
        };
    })
    .case(postSecondaryApplicantDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
            twoPersons: true,
        };
    })
    .case(postLoanFinancingDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postLoanFinancingDataAction.done, (state, action) => {
        return {
            ...state,
            loanFinancing: action.payload as LoanFinancingDto,
            loading: false,
        };
    })
    .case(postLoanFinancingDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postIncomeOtherLoansDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postIncomeOtherLoansDataAction.done, (state, action) => {
        return {
            ...state,
            incomeOtherLoans: action.payload as IncomeOtherLoansDto,
            loading: false,
        };
    })
    .case(postIncomeOtherLoansDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postLivingExpensesDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postLivingExpensesDataAction.done, (state, action) => {
        return {
            ...state,
            livingExpenses: action.payload as LivingExpensesDto,
            loading: false,
        };
    })
    .case(postLivingExpensesDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postAgreementsInfoDataAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postAgreementsInfoDataAction.done, (state, action) => {
        return {
            ...state,
            agreementsInfo: action.payload as AgreementsInfoDto,
            loading: false,
        };
    })
    .case(postAgreementsInfoDataAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(postMortgageApplication, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(postMortgageApplication.done, (state, action) => {
        return {
            ...state,
            basicInfo: action.payload,
            loading: false,
        };
    })
    .case(postMortgageApplication.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(setTwoPersonsAction, (state, action) => {
        return {
            ...state,
            twoPersons: action.payload,
        };
    })
    .case(SetCountries.done, (state, action) => {
        return {
            ...state,
            countries: action.payload,
        };
    })
    .case(SetChoiceItems.done, (state, action) => {
        return {
            ...state,
            choiceItems: action.payload,
        };
    });

export default LoanReducer;

export function setTwoPersons(hasTwoPersons: boolean) {
    return async (dispatch: ReduxDispatch) => {
        dispatch(setTwoPersonsAction(hasTwoPersons));
    };
}

export const mortgageLoading = (state: ReduxState) => {
    return state.mortgage && state.mortgage.loading;
};

export const calculateTotalGrossIncome = (state: ReduxState) => {
    const valueSelector = formValueSelector(incomeOtherLoansFormName);

    const items = valueSelector(
        state,
        "primaryGrossIncome",
        "grossIncome2",
        "grossIncome3",
        "grossIncome4"
    );

    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }

    return sum.toFixed(0).toString();
};

export const calculateTotalNetIncome = (state: ReduxState) => {
    const valueSelector = formValueSelector(incomeOtherLoansFormName);

    const items = valueSelector(
        state,
        "primaryNetIncome",
        "netIncome2",
        "netIncome3",
        "netIncome4"
    );
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};

export const calculateTotalLoan = (state: ReduxState) => {
    const valueSelector = formValueSelector(incomeOtherLoansFormName);

    const items = valueSelector(
        state,
        "housingLoanSum",
        "studentLoanSum",
        "consumerLoanSum"
    );
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};

export const calculateTotalLoanAmortization = (state: ReduxState) => {
    const valueSelector = formValueSelector(incomeOtherLoansFormName);

    const items = valueSelector(
        state,
        "housingLoanAmortization",
        "studentLoanAmortization",
        "consumerLoanAmortization"
    );
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};

export const calculateTotalTransport = (state: ReduxState) => {
    const valueSelector = formValueSelector(livingExpensesFormName);

    const items = valueSelector(state, "car", "otherTraveling");
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};
export const calculateTotalExpenses = (state: ReduxState) => {
    const valueSelector = formValueSelector(livingExpensesFormName);

    const items = valueSelector(
        state,
        "rent",
        "phoneInternet",
        "heating",
        "waterElectricity"
    );
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};
export const calculateTotalOther = (state: ReduxState) => {
    const valueSelector = formValueSelector(livingExpensesFormName);

    const items = valueSelector(
        state,
        "childCare",
        "clothing",
        "healthCare",
        "leisure",
        "others"
    );
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};
export const calculateTotalAllExpenses = (state: ReduxState) => {
    const valueSelector = formValueSelector(livingExpensesFormName);

    const items = valueSelector(
        state,
        "childCare",
        "clothing",
        "healthCare",
        "leisure",
        "others",
        "rent",
        "phoneInternet",
        "heating",
        "waterElectricity",
        "car",
        "otherTraveling",
        "food",
        "insurances"
    );
    let sum = 0;
    for (let number in items) {
        if (items.hasOwnProperty(number)) {
            sum += Number(items[number]);
        }
    }
    return sum.toFixed(0).toString();
};

export function getCountries() {
    return async (dispatch: ReduxDispatch) => {
        try {
            let result = await request.get<Country[]>(
                `${env.APP_API_HOST}/api/countries`
            );

            dispatch(SetCountries.done(result));
        } catch (error) {
            //   logger.error(error);
            throw error;
        }
    };
}

export function getAllCountries() {
    return async (dispatch: ReduxDispatch) => {
        try {
            let result = await request.get<Country[]>(
                `${env.APP_API_HOST}/api/countries/all`
            );

            dispatch(SetCountries.done(result));
        } catch (error) {
            //   logger.error(error);
            throw error;
        }
    };
}

export function getChoiceItems() {
    return async (dispatch: ReduxDispatch) => {
        try {
            let result = await request.get<SelectOptions>(
                `${env.APP_API_HOST}/api/choiceItems/loanApplication`
            );

            dispatch(SetChoiceItems.done(result));
        } catch (error) {
            //   logger.error(error);
            throw error;
        }
    };
}

export const getChoiceItemsByName = (
    state: ReduxState,
    selection: string
): SelectOptions => {
    const items = state && state.mortgage.choiceItems!;
    if (items) {
        for (const key of Object.keys(items)) {
            if (items[key].type === selection) {
                return items[key];
            }
        }
    }
    return {} as SelectOptions;
};

export const getLoanPurpose = (state: ReduxState, selection: string) => {
    const items = state && state.mortgage.choiceItems!;

    if (items) {
        for (const key of Object.keys(items)) {
            if (items[key].type === "LoanType") {
                const selected = items[key].items.find((obj: OptionGroup) => {
                    return obj.id === selection && obj;
                });
                // console.log(selected);
                return selected;
            }
        }
    }
    return {} as OptionGroup;
};

export const getCountryOptions = (state: ReduxState): Country[] => {
    return (state && state.mortgage.countries!) || ({} as Country[]);
};

export const getCollaterals = (state: ReduxState): SelectOptions[] => {
    const items = state && state.mortgage.choiceItems!;
    let returnArray: SelectOptions[] = [];
    if (items) {
        for (const key of Object.keys(items)) {
            if (
                items[key].type === "CollateralGroup" ||
                items[key].type === "CollateralType"
            ) {
                returnArray.push(items[key]);
            }
        }
    }

    return returnArray;
};

export const getDirtyAndValidMortgageForms = (state: ReduxState) => {
    let forms: { [key: string]: any } = {};

    if (
        isValid(basicInfoFormName)(state) &&
        isDirty(basicInfoFormName)(state)
    ) {
        forms[basicInfoFormName] = getFormValues(basicInfoFormName)(state);
    }
    if (
        isValid(primaryApplicantFormName)(state) &&
        isDirty(primaryApplicantFormName)(state)
    ) {
        forms[primaryApplicantFormName] = getFormValues(
            primaryApplicantFormName
        )(state);
    }
    if (
        isValid(secondaryApplicantFormName)(state) &&
        isDirty(secondaryApplicantFormName)(state)
    ) {
        forms[secondaryApplicantFormName] = getFormValues(
            secondaryApplicantFormName
        )(state);
    }
    if (
        isValid(loanFinancingFormName)(state) &&
        isDirty(loanFinancingFormName)(state)
    ) {
        forms[loanFinancingFormName] = getFormValues(loanFinancingFormName)(
            state
        );
    }
    if (
        isValid(incomeOtherLoansFormName)(state) &&
        isDirty(incomeOtherLoansFormName)(state)
    ) {
        forms[incomeOtherLoansFormName] = getFormValues(
            incomeOtherLoansFormName
        )(state);
    }
    if (
        isValid(livingExpensesFormName)(state) &&
        isDirty(livingExpensesFormName)(state)
    ) {
        forms[livingExpensesFormName] = getFormValues(livingExpensesFormName)(
            state
        );
    }
    if (
        isValid(agreementsFormName)(state) &&
        isDirty(agreementsFormName)(state)
    ) {
        forms[agreementsFormName] = getFormValues(agreementsFormName)(state);
    }

    return forms;
};



// WEBPACK FOOTER //
// ./src/ducks/mortgage.ts